import React from 'react'
import {
  faChevronDown,
  faChevronUp,
  faBox,
  faTag,
  faCalendar,
  faChartLine,
  faInfoCircle,
  faExternalLinkAlt,
} from '@fortawesome/free-solid-svg-icons'

import { ProductImage } from 'app/common/Common'
import { OrdersContext } from 'contexts/OrdersContext'
import {
  DcButton,
  DcDropDown,
  DcFlexBox,
  PixelCollapse,
  PixelCurrency,
  PixelFactoryContext,
  PixelIcon,
  PixelPageContainer,
  PixelTable,
  Section,
  SectionContent,
  $DCprimaryColor,
  DcOverlaySlider,
} from 'pixel-base'
import styled from 'styled-components'
import { get } from 'utils/api-routes.util'

// StatusBadge component for displaying product status
const StatusBadge = styled.span<{ $isActive: boolean }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${(props) => (props.$isActive ? '#d4edda' : '#f8d7da')};
  color: ${(props) => (props.$isActive ? '#155724' : '#721c24')};
  border: 1px solid ${(props) => (props.$isActive ? '#c3e6cb' : '#f5c6cb')};
`

import Variants from './Variants'
import { IPixelFactory } from '../../../../../../decl.d'
import InventoryHistoryTable from '../../inventory/components/InventoryHistoryTable'
// eslint-disable-next-line import/order
import { isMobile } from 'react-device-detect'

interface TenantInfo {
  default_currency_symbol: string
}

const tenant_info: TenantInfo = JSON.parse(
  localStorage.getItem('tenant_info') || '{"default_currency_symbol": "$"}',
)

interface ProductVariation {
  product_id: number | null
  product_image_path: string | null
  product_name: string | null
  product_sku: string | null
  product_status: string
  quantity_available: number | null
  product_price: number | null
  sale_price: number | null
  created_at: string | null
  permalink: string | null
  store_links?: StoreLink[]
}

interface StoreLink {
  store_id: number
  store_name: string
}

interface Product {
  product_id: number
  product_image_path: string
  product_name: string
  product_sku: string
  product_status: string
  quantity_available: number | null
  product_price: number
  sale_price: number
  created_at: string
  permalink: string
  variations: ProductVariation[]
  manage_inventory: boolean
  product_history?: ProductHistoryItem[]
  store_links?: StoreLink[]
}

interface ProductHistoryItem {
  product_id: number
  created_at: {
    date: string
    timezone_type: number
    timezone: string
  }
  on_hand: number | null
  committed: number | null
  available: number | null
  unavailable: number | null
  wh_id: number
  user_id: number | null
  notes: string | null
  quantity: number | null
  reason: string | null
}

interface VariationInfo {
  product_id: number | null
  product_name: string | null
  product_sku: string | null
  product_image_path: string | null
  variation_type: string | null
  variation_value: string | null
  is_variation: boolean
}

interface ProductHistoryResponse {
  data: Array<{
    variation_info: VariationInfo
    history: ProductHistoryItem[]
  }>
  _meta: {
    total: number
    page: number
    page_size: number
    pages: number
  }
  product_info: {
    product_id: number | null
    product_name: string | null
    product_sku: string | null
    product_image_path: string | null
    is_parent: boolean
    variation_count: number
  }
}

interface ProductDetailProps {
  productId: number
  setEditId: (id: number) => void
}

const columns = [
  {
    dataKey: 'variation_sku',
    name: 'Variant SKU',
    width: '20%',
    sortable: true,
    selector: (row: ProductVariation) =>
      row.product_sku ? row.product_sku : '--',
  },
  {
    dataKey: 'product_status',
    name: 'Status',
    width: '10%',
    sortable: true,
    selector: (row: ProductVariation) => (
      <StatusBadge $isActive={row.product_status === 'active'}>
        {row.product_status === 'active' ? 'Active' : 'Inactive'}
      </StatusBadge>
    ),
  },
  {
    dataKey: 'price',
    name: `Price (${tenant_info.default_currency_symbol})`,
    sortable: true,
    selector: (row: ProductVariation) =>
      row.product_price ? (
        <PixelCurrency hideSymbol={true} value={row.product_price} />
      ) : (
        '--'
      ),
  },
  {
    dataKey: 'cost_price',
    name: `Cost Price (${tenant_info.default_currency_symbol})`,
    sortable: true,
    selector: (row: ProductVariation) =>
      row.product_price ? (
        <PixelCurrency hideSymbol={true} value={row.product_price} />
      ) : (
        '--'
      ),
  },
  {
    dataKey: 'sold_count',
    name: 'Sold',
    sortable: true,
    selector: (row: ProductVariation) =>
      row.quantity_available ? row.quantity_available : '0',
  },
  {
    dataKey: 'quantity_available',
    name: 'Stock',
    sortable: true,
    selector: (row: ProductVariation) =>
      row.quantity_available ? row.quantity_available : '0',
  },
  {
    dataKey: 'store_links',
    name: 'Stores',
    width: '15%',
    sortable: false,
    selector: (row: ProductVariation) => (
      <StoreChipsContainer>
        {row.store_links && row.store_links.length > 0 ? (
          row.store_links.map((store, index) => (
            <StoreChip key={`${store.store_id}-${index}`}>
              {store.store_name}
            </StoreChip>
          ))
        ) : (
          <LinkMissingError>Link Missing</LinkMissingError>
        )}
      </StoreChipsContainer>
    ),
  },
  {
    dataKey: 'weight',
    name: 'Weight',
    sortable: true,
    selector: (row: ProductVariation) => (row.weight ? row.weight : '--'),
  },
]

const ProductDetail: React.FC<ProductDetailProps> = ({
  productId,
  setEditId,
}) => {
  const productDetailsRef = React.useRef(null)
  const stockHistory = React.useRef(null)
  const {
    showLoader,
    hideLoader,
    showPageLoader,
    hidePageLoader,
    showFailure,
    pixelConfirm,
  } = React.useContext<IPixelFactory>(PixelFactoryContext)
  const { state } = React.useContext(OrdersContext)

  const [productHistory, setProductHistory] = React.useState<
    ProductHistoryItem[]
  >([])
  const [productHistoryResponse, setProductHistoryResponse] =
    React.useState<ProductHistoryResponse | null>(null)
  const [isExpanded, setExpanded] = React.useState(true)
  const [productVariations, setProductVariations] = React.useState<
    ProductVariation[]
  >([])
  const [isExpandedVariations, setExpandedVariations] = React.useState(true)
  const [isExpandedDescription, setIsExpandedDescription] = React.useState(true)
  const [showAddNewVariantPopup, setShowAddNewVariantPopup] =
    React.useState(false)

  const [product, setProduct] = React.useState<Product | null>(null)
  const [warehouseID, setWarehouseID] = React.useState<number | 'null'>('null')
  const [selectedVariationId, setSelectedVariationId] = React.useState<
    number | 'all'
  >('all')

  const getProductData = React.useCallback(
    (product_id: number) => {
      showLoader(productDetailsRef)
      get(`/products/${product_id}`).subscribe((res: any) => {
        hideLoader(productDetailsRef)
        if (res?.response?.data) {
          setProduct(res.response.data)
          setProductVariations(res.response.data.variations || [])
        }
      })
    },
    [showLoader, hideLoader],
  )

  const getHistoryData = React.useCallback(
    (product_id: number, warehouse_id: number | 'null') => {
      showLoader(stockHistory)
      if (warehouse_id !== 'null') {
        get(
          `/inventory/${product_id}/warehouse/${warehouse_id}/history`,
        ).subscribe((res: any) => {
          hideLoader(stockHistory)
          if (res?.response?.data) {
            setProductHistoryResponse(res.response.data)
            // Flatten all history items from all variations for the table
            const allHistoryItems =
              res.response.data.data?.flatMap(
                (variation: any) => variation.history || [],
              ) || []
            setProductHistory(allHistoryItems)
          }
        })
      } else {
        hideLoader(stockHistory)
        setProductHistoryResponse(null)
        setProductHistory([])
      }
    },
    [showLoader, hideLoader],
  )

  // Fetch product data only when productId changes
  React.useEffect(() => {
    if (productId) {
      getProductData(productId)
    }
  }, [productId, getProductData])

  // Fetch history data when productId or warehouseID changes
  React.useEffect(() => {
    if (productId) {
      getHistoryData(productId, warehouseID)
    }
  }, [productId, warehouseID, getHistoryData])

  // Reset to 'all' when new data loads
  React.useEffect(() => {
    const dataArray = Array.isArray(productHistoryResponse)
      ? productHistoryResponse
      : productHistoryResponse?.data
    if (dataArray?.length) {
      setSelectedVariationId('all')
    }
  }, [productHistoryResponse])

  // Get data array from productHistoryResponse
  const dataArray = React.useMemo(() => {
    return Array.isArray(productHistoryResponse)
      ? productHistoryResponse
      : productHistoryResponse?.data
  }, [productHistoryResponse])

  // Generate dropdown options
  const variationOptions = React.useMemo(() => {
    const baseOptions = [{ name: 'All Variations', variation_id: 'all' }]

    if (!dataArray) {
      return baseOptions
    }

    const variationOptions = dataArray.map((variation, index) => ({
      name:
        variation.variation_info.product_sku ||
        `Product ID: ${
          variation.variation_info.product_id || `Variation ${index + 1}`
        }`,
      variation_id: index, // Use array index as identifier
    }))

    return [...baseOptions, ...variationOptions]
  }, [dataArray])

  // Filter history based on selected variation
  const filteredProductHistory = React.useMemo(() => {
    if (!dataArray) {
      return []
    }

    if (selectedVariationId === 'all') {
      // Return all history items from all variations
      return dataArray?.flatMap((variation) => variation.history || []) || []
    }

    // Find the selected variation by array index
    const selectedVariation = dataArray?.[selectedVariationId as number]

    return selectedVariation?.history || []
  }, [selectedVariationId, dataArray])

  if (!product) {
    return (
      <PixelPageContainer padding="20px" height="calc(100vh - 180px)">
        <DcFlexBox
          justifyContent="center"
          alignItems="center"
          height="400px"
          flexDirection="column"
          gap="16px"
        >
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <div style={{ color: '#6c757d', fontSize: '16px' }}>
            Loading product details...
          </div>
        </DcFlexBox>
      </PixelPageContainer>
    )
  }

  return (
    <>
      <PixelPageContainer padding="20px" height="calc(100vh - 380px)">
        <DcFlexBox
          flexDirection="column"
          gap="12px"
          height="auto"
          style={{ padding: '0px' }}
        >
          <SectionContent>
            <DcFlexBox
              justifyContent="space-between"
              alignItems="center"
              marginBottom="12px"
              flexDirection={isMobile ? 'column' : 'row'}
            >
              <DcFlexBox fontSize="28px" fontWeight="700" color="#2c3e50">
                {product.product_name}
              </DcFlexBox>
              <DcFlexBox gap="8px" justifyContent="flex-end">
                {product.permalink && (
                  <DcButton
                    size="sm"
                    variant="outline-primary"
                    onClick={() => {
                      window.open(product.permalink, '_blank')
                    }}
                  >
                    <PixelIcon icon={faExternalLinkAlt} />
                    &nbsp;View Link
                  </DcButton>
                )}
                <DcButton
                  size="sm"
                  onClick={() => {
                    setEditId(product.product_id)
                  }}
                >
                  Edit
                </DcButton>
              </DcFlexBox>
            </DcFlexBox>
            <DcFlexBox
              alignItems="flex-start"
              justifyContent="space-between"
              gap="20px"
            >
              <ProductImage
                style={{
                  width: '30%',
                  marginRight: '5px',
                  maxHeight: '450px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
                path={product.product_image_path}
              />
              <DcFlexBox flex="1" flexDirection="column" gap="20px">
                <InfoSection>
                  <DcFlexBox
                    alignItems="center"
                    gap="8px"
                    fontSize="16px"
                    fontWeight="600"
                    color="#2c3e50"
                    marginBottom="16px"
                  >
                    <PixelIcon icon={faBox} color={$DCprimaryColor} />
                    <span>Basic Information</span>
                  </DcFlexBox>
                  <InfoGrid>
                    <InfoItem>
                      <InfoLabel>SKU</InfoLabel>
                      <InfoValue>{product.product_sku || '--'}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Status</InfoLabel>
                      <InfoValue>
                        <StatusBadge
                          $isActive={product.product_status === 'active'}
                        >
                          {product.product_status === 'active'
                            ? 'Active'
                            : 'Inactive'}
                        </StatusBadge>
                      </InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Category</InfoLabel>
                      <InfoValue>{product.category_name || '--'}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Weight</InfoLabel>
                      <InfoValue>{product.weight || '--'}</InfoValue>
                    </InfoItem>
                  </InfoGrid>
                </InfoSection>

                <InfoSection>
                  <DcFlexBox
                    alignItems="center"
                    gap="8px"
                    fontSize="16px"
                    fontWeight="600"
                    color="#2c3e50"
                    marginBottom="16px"
                  >
                    <PixelIcon icon={faBox} color={$DCprimaryColor} />
                    <span>Linked Stores</span>
                  </DcFlexBox>
                  {product.store_links && product.store_links.length > 0 ? (
                    <StoreChipsContainer>
                      {product.store_links.map((store, index) => (
                        <StoreChip key={`${store.store_id}-${index}`}>
                          {store.store_name}
                        </StoreChip>
                      ))}
                    </StoreChipsContainer>
                  ) : (
                    <NoStoresMessage>
                      This product is not linked to any external store
                    </NoStoresMessage>
                  )}
                </InfoSection>

                <InfoSection>
                  <DcFlexBox
                    alignItems="center"
                    gap="8px"
                    fontSize="16px"
                    fontWeight="600"
                    color="#2c3e50"
                    marginBottom="16px"
                  >
                    <PixelIcon icon={faTag} color={$DCprimaryColor} />
                    <span>Pricing</span>
                  </DcFlexBox>
                  <InfoGrid>
                    <InfoItem>
                      <InfoLabel>Regular Price</InfoLabel>
                      <InfoValue>
                        {<PixelCurrency value={product.product_price} /> ||
                          '--'}
                      </InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Sale Price</InfoLabel>
                      <InfoValue>
                        {<PixelCurrency value={product.sale_price} /> || '--'}
                      </InfoValue>
                    </InfoItem>
                  </InfoGrid>
                </InfoSection>

                <InfoSection>
                  <DcFlexBox
                    alignItems="center"
                    gap="8px"
                    fontSize="16px"
                    fontWeight="600"
                    color="#2c3e50"
                    marginBottom="16px"
                  >
                    <PixelIcon icon={faChartLine} color={$DCprimaryColor} />
                    <span>Sales & Inventory</span>
                  </DcFlexBox>
                  <InfoGrid>
                    <InfoItem>
                      <InfoLabel>Items Sold</InfoLabel>
                      <InfoValue>{product.sold_count || '--'}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Stock Available</InfoLabel>
                      <InfoValue>{product.quantity_available ?? 0}</InfoValue>
                    </InfoItem>
                  </InfoGrid>
                </InfoSection>
              </DcFlexBox>
            </DcFlexBox>
          </SectionContent>

          {product.variations.length > 0 && (
            <SectionContent>
              <PixelCollapse
                defaultExpanded={true}
                onCollapseChange={(collapse) => {
                  setExpandedVariations(collapse)
                }}
                collapseController={
                  <DcFlexBox
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                    padding="8px 0"
                  >
                    <div>
                      <DcFlexBox
                        alignItems="center"
                        gap="8px"
                        fontSize="16px"
                        fontWeight="600"
                        color="#2c3e50"
                      >
                        <PixelIcon icon={faBox} color={$DCprimaryColor} />
                        <span>Product Variations</span>
                      </DcFlexBox>
                    </div>
                    <div>
                      <DcButton
                        size="sm"
                        variant="outline-primary"
                        onClick={() => {
                          setShowAddNewVariantPopup(true)
                        }}
                      >
                        Add New Variation
                      </DcButton>
                    </div>
                    <PixelIcon
                      fontSize="20px"
                      icon={isExpandedVariations ? faChevronUp : faChevronDown}
                      color={$DCprimaryColor}
                    />
                  </DcFlexBox>
                }
              >
                <PixelTable
                  columns={columns}
                  data={productVariations}
                  pagination={productVariations?.length > 25}
                />
              </PixelCollapse>
            </SectionContent>
          )}

          {product.product_description !== '' && (
            <SectionContent>
              <PixelCollapse
                defaultExpanded={true}
                onCollapseChange={(collapse) => {
                  setIsExpandedDescription(collapse)
                }}
                collapseController={
                  <DcFlexBox
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                    padding="8px 0"
                  >
                    <DcFlexBox
                      alignItems="center"
                      gap="8px"
                      fontSize="16px"
                      fontWeight="600"
                      color="#2c3e50"
                    >
                      <PixelIcon icon={faInfoCircle} color={$DCprimaryColor} />
                      <span>Product Description</span>
                    </DcFlexBox>
                    <PixelIcon
                      fontSize="20px"
                      icon={isExpandedDescription ? faChevronUp : faChevronDown}
                      color={$DCprimaryColor}
                    />
                  </DcFlexBox>
                }
              >
                <DcFlexBox
                  marginTop="16px"
                  padding="16px"
                  backgroundColor="#fff"
                  borderRadius="8px"
                  boxShadow="0 1px 3px rgba(0, 0, 0, 0.1)"
                  dangerouslySetInnerHTML={{
                    __html: product.product_description,
                  }}
                />
              </PixelCollapse>
            </SectionContent>
          )}
          {!isMobile && (
            <Section
              ref={stockHistory}
              noPadding
              className="stock-adjustment-trail"
            >
              <DcFlexBox
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                padding="8px 0"
              >
                <DcFlexBox
                  alignItems="center"
                  gap="8px"
                  fontSize="16px"
                  fontWeight="600"
                  color="#2c3e50"
                >
                  <PixelIcon icon={faCalendar} color={$DCprimaryColor} />
                  <span>Stock Adjustment History</span>
                </DcFlexBox>
                <DcFlexBox gap="16px" alignItems="center">
                  <DcDropDown
                    customLabel="warehouse"
                    placeholder={'All Warehouses'}
                    options={[
                      { name: 'All Warehouses', wh_id: 'null' },
                      ...(state.warehouses || []),
                    ].map((data: { name: string; wh_id: string }) => ({
                      label: data.name,
                      value: data.wh_id,
                    }))}
                    value={warehouseID}
                    onChange={(e) => {
                      setWarehouseID(e.target.value)
                    }}
                  />
                  <DcDropDown
                    customLabel="variation"
                    placeholder={'All Variations'}
                    options={variationOptions.map(
                      (data: {
                        name: string
                        variation_id: string | number
                      }) => ({
                        label: data.name,
                        value: String(data.variation_id),
                      }),
                    )}
                    value={String(selectedVariationId)}
                    onChange={(e) => {
                      const value = e.target.value
                      setSelectedVariationId(
                        value === 'all' ? 'all' : Number(value),
                      )
                    }}
                  />
                </DcFlexBox>
              </DcFlexBox>
              <SectionContent>
                <PixelCollapse
                  defaultExpanded={true}
                  onCollapseChange={(collapse) => {
                    setExpanded(collapse)
                  }}
                  collapseController={
                    <DcFlexBox
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                      padding="8px 0"
                    >
                      <DcFlexBox
                        alignItems="center"
                        gap="8px"
                        fontSize="16px"
                        fontWeight="600"
                        color="#2c3e50"
                      >
                        <PixelIcon icon={faChartLine} color={$DCprimaryColor} />
                        <span>Stock Adjustment Trail</span>
                      </DcFlexBox>
                      <PixelIcon
                        fontSize="20px"
                        icon={isExpanded ? faChevronUp : faChevronDown}
                        color={$DCprimaryColor}
                      />
                    </DcFlexBox>
                  }
                >
                  <InventoryHistoryTable
                    data={filteredProductHistory}
                    loading={false}
                  />
                </PixelCollapse>
              </SectionContent>
            </Section>
          )}

          {showAddNewVariantPopup && (
            <DcOverlaySlider
              width="50%"
              title={'Add New Variation'}
              isOpen={showAddNewVariantPopup}
              onClose={() => setShowAddNewVariantPopup(false)}
              padding="20px"
            >
              {showAddNewVariantPopup && (
                <SectionContent>
                  <Variants
                    product_id={productId}
                    product={product}
                    handleClose={(Data: ProductVariation) => {
                      setShowAddNewVariantPopup(false)
                      if (Data) {
                        setProductVariations([...productVariations, Data])
                      }
                    }}
                  />
                </SectionContent>
              )}
            </DcOverlaySlider>
          )}
        </DcFlexBox>
      </PixelPageContainer>
    </>
  )
}

const InfoSection = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 12px;
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const InfoLabel = styled.div`
  font-size: 12px;
  color: #7f8c8d;
  text-transform: uppercase;
`

const InfoValue = styled.div`
  font-size: 14px;
  color: #2c3e50;
  font-weight: 500;
`

const InventoryDistContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto !important;
`

const AvailableQty = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const StoreChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-top: 8px;
`

const StoreChip = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: #e3f2fd;
  color: #1976d2;
  border: 1px solid #bbdefb;
  white-space: nowrap;
`

const NoStoresMessage = styled.div`
  padding: 8px 12px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  color: #721c24;
  font-size: 14px;
  font-style: italic;
  margin-top: 8px;
`

const LinkMissingError = styled.span`
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  white-space: nowrap;
`

export default ProductDetail
